import { useMutation } from "@apollo/client";
import { Icon, LoadingSpinner } from "pepsico-ds";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getCodeGroupExportJobsMutation } from "../../graphql/mutations/codeGroupMutations";

export default function CodeGroupExportJobs({ codeGroupId }) {
  const [jobs, setJobs] = useState([]);
  const [fetchExportJobs] = useMutation(getCodeGroupExportJobsMutation);
  const [loading, setLoading] = useState(false);

  const loadJobs = useCallback(async () => {
    try {
      if (!codeGroupId) return;
      const tokenInfo = localStorage.getItem("okta-token-storage");
      const { accessToken } = JSON.parse(tokenInfo || "{}");
      const userInfo = accessToken?.claims || {};
      const gpid = userInfo?.gpid;
      if (!gpid) return;
      setLoading(true);
      const { data } = await fetchExportJobs({
        variables: { input: { codeGroupId, gpid } },
        fetchPolicy: "no-cache",
      });
      const apiJobs = data?.getCodeGroupExportJobs?.jobs || [];
      setJobs(apiJobs);
    } catch (e) {
      console.error(e);
      setJobs([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeGroupId]);

  useEffect(() => {
    loadJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeGroupId]);

  const sortedJobs = useMemo(() => {
    if (!jobs?.length) return [];
    return [...jobs].sort((a, b) => {
      const da = new Date(a?.createdAt || 0).getTime();
      const db = new Date(b?.createdAt || 0).getTime();
      return db - da; // desc
    });
  }, [jobs]);

  return (
    <div className="details-container p-2">
      <div className="display-flex align-items-center">
        <h4 className="details-container-header mr-2">Multi File Download</h4>
        <Icon
          icon="refresh"
          size="small"
          alt="reload"
          className="cursor-pointer mr-2"
          style={{ color: "#075985" }}
          onClick={() => loadJobs()}
        />
        {loading && (
          <span data-testid="export-loader">
            <LoadingSpinner size={{ height: "12px", width: "12px" }} />
          </span>
        )}
      </div>
      <div className="details-container-content-wrapper">
        <div className="codegroup-list-container">
          <table className="codegroup-list-table">
            <thead>
              <tr>
                <th>Folder Names</th>
                <th>Status</th>
                <th>Download URL</th>
              </tr>
            </thead>
            <tbody>
              {sortedJobs?.length === 0 && (
                <tr>
                  <td colSpan={3}>No export jobs found</td>
                </tr>
              )}
              {sortedJobs?.length > 0 &&
                sortedJobs.map((job) => {
                  const folderNames = (job?.folders || [])
                    .map((f) => f?.folderName)
                    .filter(Boolean)
                    .join(", ");
                  const status = job?.status || "-";
                  const canDownload = status === "COMPLETED" && job?.downloadUrl;
                  return (
                    <tr key={job?.jobId}>
                      <td>{folderNames || "-"}</td>
                      <td>{status}</td>
                      <td>
                        {canDownload ? (
                          <a
                            href={job.downloadUrl}
                            target="_blank"
                            rel="noreferrer noopener"
                            style={{ color: "#075985" }}
                          >
                            Download
                          </a>
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

CodeGroupExportJobs.propTypes = {
  codeGroupId: PropTypes.string,
};
